import { JAMSIL_MAP_IDS } from '@/constants/app';
import { MAP_ZOOM_LEVEL } from '@/constants/floor-guide';
import { useCalculateIconSize } from '@/hooks/floor-guide';
import { getMapsInstance } from '@/lib/map';
import { useGetMapDataQuery } from '@/services/queries/map';
import { useConfigStore } from '@/stores';
import { useMarkerStore } from '@/stores/markerStore';
import type { MapDataPayload } from '@/types/map';
import { applyMapLanguageToMap } from '@/utils/floor-guide';
import { mapMyLocationToMarker } from '@/utils/marker';
import { CAMERA_TYPE, type MapOptions } from 'dabeeomaps';
import type { DabeeoMap } from 'dabeeomaps';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type Error = {
    isError: boolean;
    message: string;
};

export const useMap = <T extends HTMLElement>(config: MapDataPayload) => {
    const currentMap = useRef<DabeeoMap | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error>({
        isError: false,
        message: '',
    });
    const [isMapReady, setIsMapReady] = useState<boolean>(false);
    const [mapInstance, setMapInstance] = useState<DabeeoMap | undefined>(undefined);
    const machine = useConfigStore((state) => state?.config?.server?.machine);
    const floorName = useConfigStore((state) => state?.config?.server?.floor?.name);
    const floorCode = useConfigStore((state) => state?.config?.server?.floor?.floorCode);
    const language = useConfigStore((state) => state?.config?.ui?.language);
    const mapId = useConfigStore((state) => state?.config?.server?.store?.studioMapId) || '';
    const buildingId = useConfigStore((state) => state?.config?.server?.building?.id) || '';

    const isJamsil = JAMSIL_MAP_IDS.includes(mapId);

    const { setMarkerIds } = useMarkerStore();

    const { calculateIconSize } = useCalculateIconSize();

    const { data: mapData, isLoading, isError } = useGetMapDataQuery(config);

    const getFloorsByBuildingId = useCallback(() => {
        const buildings = mapData?.dataBuilding?.getBuildings?.();
        if (!buildings) {
            return [];
        }

        return buildings.find((building) => buildingId.includes(building.id))?.floors || [];
    }, [mapData?.dataBuilding]);

    const initMap = useCallback(
        async (container: RefObject<T | null>, options?: Partial<MapOptions>) => {
            try {
                setLoading(true);
                setIsMapReady(false);
                setError({
                    isError: false,
                    message: '',
                });

                if (!container.current) {
                    setError({
                        isError: true,
                        message: 'Map container is not initialized',
                    });
                    currentMap.current = undefined;
                    setIsMapReady(false);
                    setLoading(false);
                    return;
                }

                if (!mapData) {
                    setError({
                        isError: true,
                        message: 'Map data is not available',
                    });
                    currentMap.current = undefined;
                    setIsMapReady(false);
                    setLoading(false);
                    return;
                }

                const mapInfo = mapData?.dataMapInfo?.getMapInfo?.();
                const hasOutdoor = mapInfo?.outdoor;
                currentMap.current = await getMapsInstance().showMap(
                    container.current,
                    {
                        camera: CAMERA_TYPE.D2,
                        graphicLibrary: '2D',
                        enableGeoreferencing: isJamsil ? false : !!hasOutdoor,
                        showWaterMarker: false,
                        enablePoiCollisionTest: false,
                        framerate: 30, // Giảm từ 60 xuống 30 để tối ưu GPU usage
                        spriteEnable: true,
                        spriteKeepRotation: true,
                        controlOption: {
                            zoom: machine?.zoom,
                            rotate: machine?.rotate,
                            pan: {
                                x: machine?.positionX,
                                y: machine?.positionY,
                            },
                        },
                        ...options,
                        ...(isJamsil ? { floor: floorCode } : {}),
                    },
                    mapData
                );

                if (!currentMap.current) {
                    setError({
                        isError: true,
                        message: 'Failed to initialize map',
                    });
                    setIsMapReady(false);
                    setLoading(false);
                    throw new Error('Failed to initialize map');
                }

                currentMap.current?.control?.setOption({
                    touchOption: {
                        enableRotate: false,
                    },
                    mouseOption: {
                        enableRotate: false,
                    },
                    controlRangeOption: {
                        zoom: {
                            min: MAP_ZOOM_LEVEL.MIN,
                            max: MAP_ZOOM_LEVEL.MAX,
                        },
                    },
                });

                const currentLanguage = useConfigStore.getState().config.ui.language;
                applyMapLanguageToMap(currentMap.current, currentLanguage);

                // 지도에 포함되어있는 모든 건물의 default 노출
                if (!isJamsil) {
                    await currentMap.current?.context.addAllBuilding();
                    applyMapLanguageToMap(currentMap.current, currentLanguage);
                    await currentMap.current?.context.changeFloorByName(floorName?.['ko'] || '');
                    applyMapLanguageToMap(currentMap.current, currentLanguage);
                }

                const allFloor = getFloorsByBuildingId();
                
                const markers = mapMyLocationToMarker(
                    isJamsil ? allFloor.find((item) => item.name[0].text === floorName?.['ko'])?.id : floorCode,
                    machine,
                    calculateIconSize({ width: 76, height: 76 })
                );

                const id = await currentMap.current?.markers.set({ marker: markers ? [markers] : [] });
                setMarkerIds({ myLocation: Array.isArray(id) ? id[0] : undefined });

                currentMap.current?.control?.moveTo?.({
                    position: {
                        x: machine?.positionX ?? 0,
                        y: machine?.positionY ?? 0,
                    },
                    floorId: isJamsil ? allFloor.find((item) => item.name[0].text === floorName?.['ko'])?.id : floorCode,
                    transition: true,
                });

                setError({
                    isError: false,
                    message: '',
                });
                setMapInstance(currentMap.current);
                setIsMapReady(true);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                setError({
                    isError: true,
                    message: errorMessage,
                });
                setIsMapReady(false);
            } finally {
                setLoading(false);
            }
        },
        [mapData, setMarkerIds, machine, calculateIconSize, floorName, floorCode, getFloorsByBuildingId]
    );

    const cleanupMap = useCallback(() => {
        if (currentMap.current && currentMap.current?.context?.cleanup) {
            currentMap.current?.context?.cleanup?.();
            currentMap.current = undefined;
            setMapInstance(undefined);
            setIsMapReady(false);
        }
    }, []);

    useEffect(() => {
        applyMapLanguageToMap(mapInstance, language);
    }, [mapInstance, language]);
    useEffect(() => {
        if (isError) {
            setError({
                isError: true,
                message: 'Map data is not available',
            });
            setIsMapReady(false);
        }
    }, [isError]);

    return {
        initMap,
        cleanupMap,
        currentMap: mapInstance,
        mapData,
        loading: isLoading || loading,
        error,
        isMapReady,
    };
};
